/**
 * Layer Registry - the data behind layer navigation.
 *
 * The unification this rests on: the base game is itself a layer. Characters
 * and Contractors are not tabs competing with Command; they are Field's
 * screens. That gives one rule the toolbar follows everywhere:
 *
 *   The toolbar shows exactly one layer's context at a time - never the union.
 *
 * Both the switcher and the toolbar render from this registry, so adding a
 * layer is an entry here rather than UI surgery. See
 * docs/systemsRework/LayerNavigation.md.
 *
 * WHY THIS IS A MODULE, NOT js/data/: files in js/data/ are concatenated
 * verbatim into the generated js/constants.js and therefore cannot import
 * anything. This registry has to read live state (unlock latches, currency
 * values, task status), so it needs imports. It stays free of DOM so the
 * simulator and tests can load it - see tests/game/coreModulesHeadless.test.js.
 *
 * NO DOM HERE. `activeTask()` reports WHAT the task is and which screen it
 * belongs to; the header binds HOW activating it switches screens. Keeping the
 * "how" out is what lets this be tested headlessly.
 */

import { gameState, canAccessRaid } from './gameState-core.js';
import { isCommandRevealed } from './command-core.js';
import { isSyndicateRevealed, getCharterRank } from './syndicate-core.js';
import { operationSlots } from './operations-core.js';
import { formatCompact } from './util.js';

/**
 * @typedef {'idle'|'running'|'complete'|'automated'|'none'} TaskState
 *   none      - this layer has no active task worth a button (slot hides)
 *   idle      - a task can be started
 *   running   - in progress; label carries live status
 *   complete  - finished and awaiting collection; this is what badges the switcher
 *   automated - a higher layer runs this loop hands-off (L3+)
 */

/** Field's raid, read from the same state today's raid-tab button reads. */
function fieldActiveTask() {
  const raid = gameState.currentRaid || {};
  if (!gameState.character) return { state: 'none', label: '', screen: null };
  // Another character owns the active raid — the screen is not ours to open.
  if (!canAccessRaid()) return { state: 'none', label: '', screen: null };
  if (raid.raidOwnerId && raid.raidInProgress != null) {
    return { state: 'running', label: 'Raid in Progress', screen: 'raid' };
  }
  if (raid.raidCompleted) {
    return { state: 'complete', label: 'Raid Completed', screen: 'raid' };
  }
  if (raid.currentRaidGrid && raid.selectedTier) {
    return { state: 'running', label: 'Raid in Planning', screen: 'raid' };
  }
  return { state: 'idle', label: 'New Raid', screen: 'raid' };
}

/**
 * Syndicate's active task is launching an OPERATION, not founding.
 *
 * Founding stays off the toolbar: it is irreversible and available for most of
 * an era, so a permanent "Found" button would be both a dead control and a
 * dangerous one. Launching a convoy is the opposite - repeatable, cheap to
 * decide, and the thing a player returns to the layer to do.
 *
 * `mode` splits one screen into two views. Planning a new operation and
 * watching three in flight are different jobs, and putting both on one page
 * meant the map carried every route at once. The toolbar button opens the
 * PLANNER; the screen tab opens the in-transit board.
 */
function syndicateActiveTask() {
    if (getCharterRank('operations-charter') <= 0) {
        return { state: 'none', label: '', screen: null };
    }
    const inFlight = gameState.syndicate?.operations?.length || 0;
    const slots = operationSlots();

    if (inFlight >= slots) {
        // Informational, not actionable - same role as Field's "Raid in
        // Progress". Clicking still reaches the transit board, which is the
        // useful destination when everything is already out.
        return { state: 'running', label: `Convoys out ${inFlight}/${slots}`, screen: 'operations' };
    }
    return { state: 'idle', label: 'New Operation', screen: 'operations', mode: 'plan' };
}

/**
 * Command's promotion.
 *
 * Deliberately never reports 'complete' yet. `canPromoteCore()` is a BLOCKER
 * check (no character / on marketplace / mid-raid) - it says promotion is
 * *permitted*, not that it is *worthwhile*, and it is true almost always. Using
 * it for the badge would light the switcher permanently, which is worse than
 * not badging at all.
 *
 * A real readiness signal arrives with L3's `succession-doctrine`, which ports
 * the simulator's marks-rate-peak policy into the game so the Command panel and
 * the automation share one computation (docs/layers/3-syndicate/design.md §4.4). Until
 * then Command reports 'idle', and the spec hides the slot when idle.
 */
function commandActiveTask() {
  // 'none', not 'idle': the spec hides Command's slot when idle, and promotion
  // is started from the Promotion screen rather than the toolbar. Showing a
  // permanent "Promote" button here would be the dead control the active-task
  // slot exists to avoid. This becomes 'complete' -> "Promotion ready" once a
  // readiness signal exists (L3 succession-doctrine).
  return { state: 'none', label: '', screen: null };
}

/**
 * Has this account ever run a character, i.e. is an empty roster a BRAND NEW
 * account or one between characters?
 *
 * The two states look identical from `gameState.characters` alone but want
 * opposite answers: a new account should see nothing but the creator, while an
 * account that just promoted or founded needs Field reachable so it can get BACK
 * to the creator.
 *
 * Every signal here is a one-way latch, so this can never flip back to false and
 * strand a player: a tour past the first, an enshrined Legend, or a founding.
 */
export function hasRunACharacter() {
  return (gameState.command?.legends?.length || 0) > 0
    || (gameState.command?.tour || 1) > 1
    || (gameState.syndicate?.founded || 0) > 0;
}

/**
 * The layers, in ladder order.
 *
 * `accent` is per-layer and configurable on purpose - the switcher takes the
 * CURRENT layer's accent so it reads as "you are here", rather than being
 * permanently violet (Command's identity colour). The alert state (badge/pulse)
 * stays one neutral colour across layers so "something needs you" never reads
 * as a layer identity.
 *
 * `screens[].tab` maps onto the existing `data-tab` plumbing, so screens are
 * backed by the tab machinery that already works rather than a new renderer.
 */
export const LAYERS = [
  {
    id: 'field',
    // Label is display-only; `id` stays 'field' because saves, lastScreenByLayer
    // and every test key off it. Renaming the id would strand saved UI state.
    label: 'The Field',
    icon: '⚔️',
    accent: '#7cc4ff',
    unlocked: () => true,
    // `visible` is declarative on purpose. This used to be imperative — the old
    // updateTabVisibility() added a `hidden` class — which stopped working the
    // moment the toolbar started re-rendering: the next render simply rebuilt
    // the buttons without it. Anything the renderer owns has to be expressed as
    // state the renderer reads.
    //
    // On a BRAND NEW account the creation screen is the only view, so Field
    // offers no navigation. Between characters is a different state and needs a
    // different answer: `#character-tab` hosts the creator as well as the
    // roster, so gating it on `gameState.character` left a promoted or
    // post-founding account with no visible Field screen at all — switchLayer
    // had no tab to click, and "The Field" went nowhere.
    screens: [
      { id: 'character', label: 'Characters', icon: '🧑', tab: 'character', visible: () => !!gameState.character || hasRunACharacter() },
      // Contractors stays strict: a hiring pool with nobody to hire for is a
      // genuinely dead screen, not a route to the creator.
      { id: 'contractors', label: 'Contractors', icon: '🤝', tab: 'contractors', visible: () => !!gameState.character },
      // The Archive appears with the first Collapse Key and never leaves — the
      // record is account-level, so it outlives every character.
      //
      // Gated on HAVING one rather than on having a character, and that is not
      // just dead-control hygiene: Field's toolbar row measured 753 of 768px
      // with two screens, so a third is a real width cost and it should not be
      // paid until there is something behind it. Below 900px the toolbar drops
      // labels for icons, which is what keeps three Field screens inside the
      // tablet budget once it does appear.
      {
        id: 'archive', label: 'Archive', icon: '🗝️', tab: 'archive',
        visible: () => (gameState.player?.collapseKeys?.length || 0) > 0,
      },
    ],
    currencies: [
      // `compact` bounds the chip at 5 characters. Credits are the one value
      // here that grows without limit, and the chip grew with it.
      { id: 'credits', icon: '💰', value: () => gameState.player?.credits ?? 0, compact: true },
      {
        id: 'energy',
        icon: '⚡',
        // Already bounded by maxEnergy, and it is a ratio - abbreviating would
        // cost clarity to save nothing.
        value: () => `${gameState.player?.energy ?? 0}/${gameState.player?.maxEnergy ?? 0}`,
      },
    ],
    activeTask: fieldActiveTask,
  },
  {
    id: 'command',
    label: 'Command',
    icon: '🎖️',
    accent: '#b18cff',
    // Reveal latch, not a live check - Command stays available between tours,
    // which is exactly when upgrades get bought.
    unlocked: () => isCommandRevealed(),
    screens: [
      { id: 'command', label: 'Promotion', icon: '🎖️', tab: 'command' },
      { id: 'command-vault', label: 'Vault', icon: '🏦', tab: 'command-vault' },
    ],
    currencies: [
      // Marks accumulate across every tour, so they grow without limit too.
      { id: 'marks', icon: '🎖️', value: () => gameState.command?.marks ?? 0, compact: true },
    ],
    activeTask: commandActiveTask,
  },
  {
    id: 'syndicate',
    label: 'Syndicate',
    icon: '📜',
    accent: '#e0a84c', // seal-gold; Command owns violet
    unlocked: () => isSyndicateRevealed(),
    // 'Charter' rather than 'Syndicate': the layer name is already in the
    // switcher, so repeating it here would spend header width saying nothing.
    // It is also short, which matters - at 1024 the toolbar's spare room is
    // ~117px and a long label is what would actually break portrait.
    screens: [
      { id: 'charter', label: 'Charter', icon: '📜', tab: 'syndicate' },
      // Operations appear only once licensed. A screen for a system the player
      // cannot use yet is the dead control the `visible` predicate exists to
      // avoid — and unlike Field's screens this one is bought, not unlocked by
      // progression, so it can also disappear again on a fresh era.
      {
        id: 'operations', label: 'Operations', icon: '🗺️', tab: 'operations',
        visible: () => (gameState.syndicate?.upgrades?.['operations-charter'] || 0) > 0,
      },
      { id: 'syndicate-stores', label: 'Stores', icon: '📦', tab: 'syndicate-stores' },
    ],
    currencies: [
      // Writs accumulate across every founding, so they need the same compact
      // treatment as marks and credits.
      { id: 'writs', icon: '📜', value: () => gameState.syndicate?.writs ?? 0, compact: true },
      // Energy appears here even though Command deliberately hides it, because
      // the rule is "show what is ACTIONABLE in this layer" rather than "hide
      // everything from below". Operations spend energy, so a player planning
      // one needs to see it without leaving the layer.
      {
        id: 'energy',
        icon: '⚡',
        value: () => `${gameState.player?.energy ?? 0}/${gameState.player?.maxEnergy ?? 0}`,
      },
    ],
    activeTask: syndicateActiveTask,
  },
];

/** @returns {Array} every layer, unlocked or not (locked ones render dimmed). */
export function getLayers() {
  return LAYERS;
}

/** @param {string} id @returns {object|null} */
export function getLayer(id) {
  return LAYERS.find(l => l.id === id) || null;
}

/** @returns {Array} layers the player has reached. */
export function unlockedLayers() {
  return LAYERS.filter(l => {
    try { return l.unlocked(); } catch { return false; }
  });
}

/**
 * The layer the toolbar is currently showing. Falls back to Field, which is
 * always unlocked, so this can never return a layer the player cannot see.
 */
export function currentLayerId() {
  const id = gameState.ui?.currentLayer;
  const layer = id ? getLayer(id) : null;
  if (layer && layer.unlocked()) return layer.id;
  return 'field';
}

/**
 * Switch layers. Refuses locked layers so a stale saved id cannot strand the
 * player on a screen they should not have.
 * @returns {boolean} whether the switch happened
 */
export function setCurrentLayer(id) {
  const layer = getLayer(id);
  if (!layer || !layer.unlocked()) return false;
  if (!gameState.ui) gameState.ui = {};
  gameState.ui.currentLayer = id;
  return true;
}

/**
 * Screens for a layer (defaults to the current one), filtered by each screen's
 * optional `visible` predicate. A screen with no predicate always shows.
 */
export function screensFor(layerId = currentLayerId()) {
  return (getLayer(layerId)?.screens ?? []).filter(s => !s.visible || s.visible());
}

/**
 * Currencies for a layer. Layer-scoped, NOT accumulated: credits and energy are
 * not actionable in Command, so they do not appear there. This is the single
 * change that keeps the right-hand cluster from growing once per layer.
 */
export function currenciesFor(layerId = currentLayerId()) {
  return (getLayer(layerId)?.currencies ?? []).map(c => {
    const raw = c.value();
    const value = c.compact ? formatCompact(raw) : raw;
    // `exact` is null unless abbreviating actually hid something, so the header
    // only carries a tooltip where there is something to reveal. Hover is not
    // reachable on tablet - a supported target - so this is a supplement to the
    // 100,000 exact-digits floor, never the thing that makes it usable.
    const exact = c.compact && value !== String(raw) ? Number(raw).toLocaleString() : null;
    return { id: c.id, icon: c.icon, value, exact };
  });
}

/** Active-task summary for a layer. */
export function activeTaskFor(layerId = currentLayerId()) {
  const layer = getLayer(layerId);
  if (!layer) return { state: 'none', label: '', screen: null };
  return layer.activeTask();
}

/**
 * Unlocked layers OTHER than the current one with a completed cycle - what the
 * switcher badge counts.
 *
 * Counts LAYERS needing attention, not tasks, so the badge stays stable as
 * layers and task counts grow (Operations alone can run 3 at once).
 */
export function layersNeedingAttention(currentId = currentLayerId()) {
  return unlockedLayers()
    .filter(l => l.id !== currentId && l.activeTask().state === 'complete')
    .map(l => l.id);
}
